
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxySnackbarModule } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { ACCOUNT_GROUP_ID_TOKEN } from '../../tokens';
import { KnowledgeSourceWithSuggestedFlag } from '../../ai-knowledge-util';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { WebChatWidgetService } from '../../services/web-chat-widget.service';

import { KnowledgeSourceConfig, KnowledgeSourceKey } from '@vendasta/embeddings';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-business-profile',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    GalaxySnackbarModule,
    MatInputModule,
    MatStepperModule,
    MatIconModule,
    GalaxyTooltipModule,
    ApplicationSelectorComponent
],
  providers: [AiKnowledgeService, WebChatWidgetService],
  templateUrl: './business-profile.component.html',
  styleUrls: ['./business-profile.component.scss'],
})
export class BusinessProfileComponent implements OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('applicationSelector') applicationSelector!: ApplicationSelectorComponent;

  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly webChatWidgetService = inject(WebChatWidgetService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly isLoading = signal(true);

  knowledgeSource: KnowledgeSourceWithSuggestedFlag | null = null;
  knowledgeCreated = new EventEmitter<string>();
  stepChange = new EventEmitter<string>();
  readonly otherAIDisclaimer = signal('');

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<BusinessProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {}

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    if (this.data?.knowledgeSourceKey) {
      await this.knowledgeService
        .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
        .then(
          (knowledgeSource) => {
            this.knowledgeSource = knowledgeSource as KnowledgeSourceWithSuggestedFlag;
          },
          (_) => {
            this.knowledgeSource = null;
          },
        );

      this.otherAIDisclaimer.set(
        await this.knowledgeService.getOtherAIDisclaimerText(
          this.data.knowledgeSourceKey.knowledgeSourceId,
          this.data.currentAppId,
        ),
      );

      if (!this.knowledgeSource) {
        this.knowledgeSource = this.knowledgeService.buildBusinessProfileKnowledgeSource(this.data.isSuggested);
        this.cdr.detectChanges();
      }
    }
    this.isLoading.set(false);
  }

  async submit(): Promise<void> {
    if (!this.knowledgeSource) {
      await this.knowledgeService.createKnowledgeSource('Business Profile', {
        businessProfileConfig: {
          accountGroupId: this.accountGroupId(),
        },
      });
    }
    await this.applicationSelector.setAppsForKnowledgeSource();
    this.close();
  }

  hasUnsavedChanges(): boolean {
    return this.applicationSelector?.hasUnsavedChanges;
  }

  shouldSubmit(): boolean {
    return this.hasUnsavedChanges() || !this.knowledgeSource || this.applicationSelector?.forceSubmit;
  }

  customButton = {
    text: 'BUSINESS_PROFILE.MANAGE_BUSINESS_PROFILE',
    link: this.knowledgeService.buildBusinessProfileUrl(),
  };

  async createWebChatWidget(): Promise<void> {
    await this.webChatWidgetService.createWebChatWidget({
      dialogRef: this.dialogRef,
      knowledgeSource: this.knowledgeSource,
      applicationSelector: this.applicationSelector,
    });
  }

  getTitle(): string {
    if (!this.data?.knowledgeSourceKey) {
      return 'ADD_KNOWLEDGE';
    }
    return 'EDIT_KNOWLEDGE.TITLE';
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.dialogRef.close();
    });
  }
}
